import React, { FC, useState, useEffect, useCallback } from 'react'
import cx from 'classnames'
import { Router, RouteComponentProps, Redirect } from '@reach/router'
import { Position } from '@blueprintjs/core'
import get from 'lodash/fp/get'
import { get as lodashGet } from 'lodash'
import { Flex, PageContent, ModalLayer, IconButton } from '@ui'
import { useUser, IUseCurrentUser } from '@hooks/useUser'
import { useFloor } from '@providers/mapProvider'
import {
  Flag,
  IAssignmentPlan,
  IDepartment,
  IEmployeeDetails,
  IFloor,
  ISpace,
  ITeam,
  Role,
} from '@api/types'
import EmployeeCatalog from 'components/EmployeeCatalog'
import UnderActiveConstruction from 'components/UnderActiveConstruction'
import WorkplaceGroupCatalog from 'components/WorkplaceGroupManagement/Catalog/WorkplaceGroupCatalog'
import NeighborhoodCatalog from 'components/NeighborhoodManagement/Catalog/NeighborhoodCatalog'
import ToggleBookDeskButton from 'components/ToggleBookDeskButton/ToggleBookDeskButton'
import { useAppData } from '@providers'
import { withLDConsumer } from 'launchdarkly-react-client-sdk'
import { LDProps } from 'launchdarkly-react-client-sdk/lib/withLDConsumer'
import { DivProps } from '@commonTypes'
import useLocations from '@ui/selects/LocationSelect/useLocations'
import { navigateWithBasepath as navigate } from '../../App'
import Appbar from '../Appbar'
import AdminSearch from '../EmployeeManagement/AdminSearch'
import Toolbar from './Toolbar'
import Map from './Map'
import css from './adminFloorPlan.module.scss'
import MultiSelectSpaceTab from './MultiSelectSpaceTab'
import { Mode } from '@ui/selects/LocationSelect'
import useSelection from '@ui/selects/LocationSelect/useSelection'
import SpaceCatalog from './SpaceCatalog'
import useScenarioGroups from 'components/APWidget/useScenarioGroups'
import { getPlanById } from 'components/APWidget'
import useLocalStorage from 'use-local-storage'
import FinderMapFilters from 'components/FinderMapFilters/FinderMapFilters'
import RightDrawer from 'components/DrawerGQL/RightDrawer'
import ViewFiltersDrawer from 'components/ViewFiltersDrawer'
import {
  hasDeptManageRights,
  hasLocationManageRights,
  isAdmin,
  isDepartmentAdmin,
  isFinder,
  isLocationAdmin,
  isPowerAdmin,
  isSpaceCaptain,
} from '@utils'
import useViewFilters from 'components/ViewFiltersDrawer/useViewFilters'

interface AdminFloorMapProps extends RouteComponentProps {
  apId?: string
  floorId: string
  pinLeftNav: boolean
  spaceId?: string
}

let persistedState: { apId: string | null; floorId: string | null; spaceId?: string | null } = {
  apId: null,
  floorId: null,
}

export const parseInputObject = (object: any) => {
  const getSpaceObjectDetails = (spaceObject: any) => {
    const {
      spaceAssignment: { employee, employees, department, team },
      floor: { id },
    } = spaceObject
    return {
      space: spaceObject,
      empl: employee,
      empls: employees,
      dept: department,
      team,
      floorId: id,
    }
  }

  const getEmployeeObjectDetails = (employeeObject: any) => {
    const { spaceAssignment, dept, team } = employeeObject
    const spaceObject = spaceAssignment?.space
      ? {
          ...spaceAssignment.space,
          spaceAssignment: {
            neighborhood: spaceAssignment.neighborhood,
          },
        }
      : null

    return {
      space: spaceObject,
      empl: employeeObject,
      dept,
      team,
    }
  }
  const isSpaceObject = object?.floor
  return isSpaceObject ? getSpaceObjectDetails(object) : getEmployeeObjectDetails(object)
}

const getSpaceCaptainCalPermissions = ({
  floorId,
  user,
  dept,
  floor,
  team,
}: {
  floorId: string
  user: IEmployeeDetails
  dept: IDepartment
  floor: any
  team: ITeam
}) => {
  return floorId
    ? hasLocationManageRights(user, floorId) &&
        hasDeptManageRights(user, {
          spaceAssignment: { department: dept, team },
        } as unknown as ISpace)
    : hasLocationManageRights(user, floor.id)
}

export const getPermissions = (
  dept: IDepartment,
  team: ITeam,
  floorId: string,
  user: IEmployeeDetails,
  floor: { id: string }
) => {
  const getDefaultPermissions = () => ({
    canChangeCalendar: false,
  })
  const getAdminPermissions = () => ({
    canChangeCalendar: true,
  })
  const getPowerAdminPermissions = () => ({
    ...getAdminPermissions(),
  })

  const getDeptAdminPermissions = () => ({
    canChangeCalendar: hasDeptManageRights(user, {
      spaceAssignment: { department: dept, team },
    } as unknown as ISpace),
  })

  const getLocAdminPermissions = () => ({
    canChangeCalendar: floorId
      ? hasLocationManageRights(user, floorId)
      : hasLocationManageRights(user, floor.id),
  })

  const getSpaceCaptainPermissions = () => ({
    canChangeCalendar: getSpaceCaptainCalPermissions({
      floorId,
      user,
      dept,
      floor,
      team,
    }),
  })
  const permissionsWithValidators = [
    {
      validator: (role: Role) => isAdmin(role) && !isPowerAdmin(role),
      getPermissions: getAdminPermissions,
    },
    { validator: isPowerAdmin, getPermissions: getPowerAdminPermissions },
    { validator: isDepartmentAdmin, getPermissions: getDeptAdminPermissions },
    { validator: isLocationAdmin, getPermissions: getLocAdminPermissions },
    { validator: isSpaceCaptain, getPermissions: getSpaceCaptainPermissions },
    { validator: () => true, getPermissions: getDefaultPermissions },
  ]
  const role = lodashGet(user, 'role') as unknown as Role
  return permissionsWithValidators?.find(({ validator }) => validator(role))?.getPermissions()
}

export default withLDConsumer()(AdminFloorMap)

function AdminFloorMap({
  apId,
  floorId,
  spaceId,
  navigate,
  flags,
  pinLeftNav,
}: AdminFloorMapProps & LDProps) {
  const primaryPlanId = useAppData().primaryAssignmentPlan.id
  const user = useUser()
  const floor = useFloor()
  const regions = useLocations()
  const defaultFloorId = user.user?.settings?.defaultFloor ?? getFloorIdByRegions(regions)
  const initApId = apId || persistedState.apId || primaryPlanId
  const initFloorId =
    floorId ||
    user.user?.settings?.defaultFloor ||
    persistedState.floorId ||
    (getCurrentUserFloorId(user) as string)
  const { groups } = useScenarioGroups()
  const primaryPlan = useAppData().primaryAssignmentPlan
  const selectedPlan = apId ? getPlanById(groups, apId) : primaryPlan
  const [localStorageItem, setLocalStorageItem] = useLocalStorage('locate-space', '')
  const [mapViewSetting, setMapViewSetting] = useState<any>({
    mapViewSettings: [],
    updateMapViewSettings: () => {},
  })
  const [assetFilterSetting, setAssetFilterSetting] = useState<any>({
    assetFilterSettings: [],
    updateAssetFilterSettings: () => {},
  })
  const [ap, setAp] = useState(selectedPlan)

  const viewFiltersData = useViewFilters()
  enum Toggle {
    DESK,
    MEETING_ROOM,
  }
  useEffect(() => {
    floor.init?.({ apId: initApId, floorId: initFloorId })
    floor.selection?.clear()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const [splitScreen, setSplitScreen] = useState(false)
  const [unseatedDrawer, setUnseatedDrawer] = useState(false)
  const [showFilters, setShowFilters] = useState(false)
  const [showViewFilters, setShowViewFilters] = useState(false)
  useEffect(() => {
    if (floor) {
      persistedState = {
        apId: floor.apId,
        floorId: floor.floorId,
      }
    }
  }, [floor])

  useEffect(() => {
    setShowViewFilters(false)
  }, [floor.floorId])

  useEffect(() => {
    setAp(selectedPlan)
  }, [selectedPlan])

  useEffect(() => {
    if (localStorageItem) {
      floor.selection?.add([localStorageItem])
      setLocalStorageItem(undefined)
    }
  }, [])

  useEffect(() => {
    if (showViewFilters) {
      setShowFilters(false)
      setSplitScreen(false)
      floor.selection?.clear()
    }
  }, [showViewFilters])

  const onFloorChange = (newFloor: IFloor) => {
    setSplitScreen(false)
    floor.setFloorId?.(newFloor.id)

    if (floor.apId && !isFinder(user?.user?.role)) {
      navigate?.(`/floor_plan/${newFloor.id}/ap/${floor.apId}`)
    }
  }

  const fetchBookableDesks = (action: number) => {
    if (action === Toggle.DESK) floor.setIsBookDeskPage?.(floor.floorId)
    else floor.setIsBookMeetingRoomPage?.(floor.floorId)
  }

  if (!flags?.[Flag.FLOOR_PLAN]) {
    return <div>Floor Plan is disabled</div>
  }

  if (!floorId) {
    const redirectFloorId = persistedState.floorId || defaultFloorId || getCurrentUserFloorId(user)

    if ((apId || persistedState.apId) && redirectFloorId) {
      return (
        <Redirect
          noThrow
          to={`/floor_plan/${redirectFloorId}/ap/${apId || persistedState.apId || primaryPlanId}`}
        />
      )
    }
  }

  return (
    <>
      <Appbar title="Floor plan"></Appbar>
      <PageContent>
        <Toolbar
          role={user.user?.role}
          splitScreen={splitScreen}
          pinLeftNav={pinLeftNav}
          bookDeskComponent={
            <ToggleBookDeskButton
              assignmentPlan={ap}
              toggleBookDesk={(action: number) => fetchBookableDesks(action)}
            />
          }
          searchComponent={
            <AdminSearch
              navigate={navigate}
              setShowFilters={setShowFilters}
              setShowViewFilters={setShowViewFilters}
              showFilters={showFilters}
              setUnseatedDrawer={setUnseatedDrawer}
              setSplitScreen={setSplitScreen}
            />
          }
          filtersComponent={
            <FinderMapFilters
              updateMapViewSettings={mapViewSetting?.updateMapViewSettings}
              mapViewSettings={mapViewSetting?.mapViewSettings}
              isBookDeskPage={floor.isBookDeskPage}
              isBookMeetingRoomPage={floor.isBookMeetingRoomPage}
              assetFilterSettings={assetFilterSetting?.assetFilterSettings}
              updateAssetFilterSettings={assetFilterSetting?.updateAssetFilterSettings}
            />
          }
          onFloorChange={(newFloor: IFloor) => onFloorChange(newFloor)}
          onApChange={(ap: IAssignmentPlan) => {
            setAp(ap)
            if (navigate) {
              floor.setApId?.(ap.id)
              if (floor.floorId) {
                navigate(`/floor_plan/${floor.floorId}/ap/${ap.id}`)
              }
            }
          }}
        />
        <Map
          key={`${floorId}-${apId}`}
          {...{
            splitScreen,
            showViewFilters,
            setShowViewFilters,
            floor,
            user: user.user,
            pinLeftNav,
          }}
          mapViewSetting={(mapViewSetting) => setMapViewSetting(mapViewSetting)}
          assetFilterSetting={(assetFilterSetting) => setAssetFilterSetting(assetFilterSetting)}
        />
        {!!floor.floorId && (
          <Aside
            {...{
              splitScreen,
              setSplitScreen,
              floorId: floor.floorId,
              setUnseatedDrawer,
              setShowFilters,
              setShowViewFilters,
              role: user?.user?.role,
              user: user,
            }}
          />
        )}
        <RightDrawer
          assignmentPlanId={selectedPlan?.id}
          floor={floor}
          setOpen={setUnseatedDrawer}
          open={unseatedDrawer}
          setSplitScreen={setSplitScreen}
          setShowFilters={setShowFilters}
        />

        {showViewFilters && (
          <ViewFiltersDrawer
            setShowViewFilters={setShowViewFilters}
            viewFiltersData={viewFiltersData}
            mapViewSetting={mapViewSetting}
          />
        )}
      </PageContent>
      {/* eslint-disable @typescript-eslint/ban-ts-comment */}
      {/* @ts-ignore */}
      <Router primary={false} navigate={navigate}>
        <ModalLayer path="*" />
      </Router>
    </>
  )
}

enum Tabs {
  EMPLOYEES,
  NEIGHBORHOODS,
  WORKPLACE_GROUPS,
  MOVES,
  SPACES,
  MULTI_SELECT_SPACE,
  BOOKED_TIMES,
}

function Aside({
  splitScreen,
  setSplitScreen,
  floorId,
  setUnseatedDrawer,
  setShowFilters,
  setShowViewFilters,
  role,
  user,
}: {
  splitScreen: boolean
  setSplitScreen: (value: boolean) => void
  floorId: string | null
  setUnseatedDrawer: (value: boolean) => void
  setShowFilters: (value: boolean) => void
  setShowViewFilters: (value: boolean) => void
  role: Role | undefined
  user: any
}) {
  const floor = useFloor()

  const selectedSpaceCount = floor.selection?.count
  const isMultiSpaceSelected = selectedSpaceCount && selectedSpaceCount >= 2 ? true : false

  const regions = useLocations()
  const floorDetails = useSelection(Mode.SINGLE_FLOOR, floorId, regions)

  const [activeTab, setActiveTab] = useState<Tabs>(Tabs.EMPLOYEES)
  const [updateDefaultImage, setUpdateDefaultImage] = useState(false)

  const isSpaceSelected =
    !(floor.isBookDeskPage || floor.isBookMeetingRoomPage) &&
    floor.selection?.count === 1 &&
    Object.keys(floor.selection?.items || [])[0] &&
    !isFinder(role)

  const activateSpaceTab = useCallback(() => {
    isSpaceSelected && setActiveTab(Tabs.SPACES)
    isMultiSpaceSelected && setActiveTab(Tabs.MULTI_SELECT_SPACE)
    setSplitScreen(true)
    setUnseatedDrawer(false)
    setShowFilters(false)
    setShowViewFilters(false)
  }, [setSplitScreen, isSpaceSelected, isMultiSpaceSelected, setUnseatedDrawer, setShowFilters])

  // Active the space tab by a space selecting
  useEffect(() => {
    if (!isSpaceSelected && !isMultiSpaceSelected) {
      setSplitScreen(false)
      return
    }
    activateSpaceTab()
  }, [activateSpaceTab, isSpaceSelected, isMultiSpaceSelected, setSplitScreen])

  const allSelectedSpacesIds = floor?.selection?.items ? Object.keys(floor?.selection?.items) : []

  return (
    <AsideContainer splitScreen={splitScreen} isMultiSpaceSelected={isMultiSpaceSelected}>
      {isSpaceSelected && (
        <>
          <div className={css.aside_tabs}>
            <IconButton
              style={{ marginBottom: 8 }}
              round
              minimal
              large
              iconName="user-friends"
              tooltip="Employee Catalog"
              tooltipPosition={Position.LEFT}
              active={splitScreen && activeTab === Tabs.EMPLOYEES}
              onClick={() => {
                if (!splitScreen || activeTab !== Tabs.EMPLOYEES) {
                  setActiveTab(Tabs.EMPLOYEES)
                  setSplitScreen(true)
                }
              }}
            />
            <IconButton
              style={{ marginBottom: 8 }}
              round
              minimal
              large
              iconName="users"
              tooltip="Neighborhoods"
              tooltipPosition={Position.LEFT}
              active={splitScreen && activeTab === Tabs.NEIGHBORHOODS}
              onClick={() => {
                if (!splitScreen || activeTab !== Tabs.NEIGHBORHOODS) {
                  setActiveTab(Tabs.NEIGHBORHOODS)
                  setSplitScreen(true)
                }
              }}
            />
            <IconButton
              style={{ marginBottom: 8 }}
              round
              minimal
              large
              iconName="users"
              tooltip="Workplace Groups"
              tooltipPosition={Position.LEFT}
              active={splitScreen && activeTab === Tabs.WORKPLACE_GROUPS}
              onClick={() => {
                if (!splitScreen || activeTab !== Tabs.WORKPLACE_GROUPS) {
                  setActiveTab(Tabs.WORKPLACE_GROUPS)
                  setSplitScreen(true)
                }
              }}
            />
            <IconButton
              style={{ marginBottom: 8 }}
              round
              minimal
              large
              iconName="person-carry"
              tooltip="Moves Orders"
              tooltipPosition={Position.LEFT}
              active={splitScreen && activeTab === Tabs.MOVES}
              onClick={() => {
                if (!splitScreen || activeTab !== Tabs.MOVES) {
                  setActiveTab(Tabs.MOVES)
                  setSplitScreen(true)
                }
              }}
            />
            <IconButton
              style={{ marginBottom: 8 }}
              round
              minimal
              large
              disabled={!isSpaceSelected}
              iconName="chair-office"
              tooltip="Spaces"
              tooltipPosition={Position.LEFT}
              active={splitScreen && activeTab === Tabs.SPACES}
              onClick={() => {
                if (!splitScreen || activeTab !== Tabs.SPACES) {
                  activateSpaceTab()
                }
              }}
            />
          </div>
          <AsideTabContent splitScreen={splitScreen}>
            {splitScreen && activeTab === Tabs.SPACES && (
              <SpaceCatalog
                updateDefaultImage={updateDefaultImage}
                floorDetails={floorDetails}
                setUpdateDefaultImage={setUpdateDefaultImage}
                splitScreen={splitScreen}
                setSplitScreen={setSplitScreen}
                isSpaceSelected={isSpaceSelected}
                user={user}
              />
            )}
            {splitScreen && activeTab === Tabs.EMPLOYEES && (
              <EmployeeCatalog
                filterable
                draggable
                onSelect={(employee) => navigate(`?employeeId=${employee.id}`, {})}
              />
            )}
            {activeTab === Tabs.NEIGHBORHOODS && (
              <NeighborhoodCatalog floorId={floorId ? floorId : undefined} />
            )}
            {activeTab === Tabs.WORKPLACE_GROUPS && <WorkplaceGroupCatalog />}
            {activeTab === Tabs.MOVES && <UnderActiveConstruction small uri="Moves Orders" />}
            {activeTab === Tabs.BOOKED_TIMES && (
              <UnderActiveConstruction small uri="Booked Times" />
            )}
          </AsideTabContent>
        </>
      )}
      {isMultiSpaceSelected && (
        <MultiSelectSpaceTab
          spaces={floor?.spaces}
          selectedItems={allSelectedSpacesIds}
          setSplitScreen={setSplitScreen}
        />
      )}
    </AsideContainer>
  )
}

function AsideTabContentHeader({
  activeTab,
  setSplitScreen,
}: {
  activeTab: Tabs
  setSplitScreen: (value: boolean) => void
}) {
  let title = ''

  switch (activeTab) {
    case Tabs.NEIGHBORHOODS:
      title = 'Neighborhoods'
      break

    case Tabs.WORKPLACE_GROUPS:
      title = 'Workplace Groups'
      break

    case Tabs.MOVES:
      title = 'Move Orders'
      break

    default:
      title = 'Employee Catalog'
  }

  return (
    <Flex align="center" justify="space-between" style={{ height: 50, paddingRight: 5 }}>
      <h4 style={{ marginLeft: 10 }}>{title}</h4>
      <IconButton
        round
        minimal
        iconName="times"
        tooltip="Close"
        onClick={() => setSplitScreen(false)}
      />
    </Flex>
  )
}

function getCurrentUserFloorId(user: IUseCurrentUser) {
  return (
    user.user?.employee.spaceAssignment?.space.floor.id ||
    user.user?.employee.secondarySpaceAssignments[0]?.space.floor.id ||
    user.user?.employee.neighborhoods[0]?.floor.id
  )
}

type ScreenProps = DivProps & {
  splitScreen: boolean
  isMultiSpaceSelected?: boolean
}

const AsideContainer: FC<React.PropsWithChildren<ScreenProps>> = ({
  className,
  splitScreen,
  isMultiSpaceSelected,
  style,
  ...props
}) => {
  return (
    <div
      className={cx(css.aside_container, className)}
      style={{
        width: splitScreen ? (isMultiSpaceSelected ? 330 : 375) : 0,
        ...style,
      }}
      {...props}
    />
  )
}

const AsideTabContent: FC<React.PropsWithChildren<ScreenProps>> = ({
  className,
  splitScreen,
  style,
  ...props
}) => {
  return (
    <div
      className={cx(css.aside_tab_content, css.aside_tabs, className)}
      style={{ right: splitScreen ? 0 : -300, ...style }}
      {...props}
    />
  )
}

const getFloorIdByRegions = get('[0].cities[0].buildings[0].floors[0].id')
